import { LocalizedText, useSourceLanguage } from "../i18n/LocalizedCopy";
import React from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { useOptionalFeatureRegistry } from "../feature-status/FeatureRegistryContext";
import { isFeatureNavigable } from "../feature-status/registry";
import { NotificationCenterProvider } from "./NotificationCenterProvider";

export const NOTIFICATION_CENTER_FEATURE_KEY = "notification-center";
export const DEFAULT_NOTIFICATION_POLL_INTERVAL_MS = 45_000;
const NOTIFICATION_CENTER_RUNTIME_STATUSES = new Set([
  "ACTIVE",
  "BETA",
  "PRIVATE_BETA",
]);

function configuredPollInterval() {
  const configured = Number(process.env.REACT_APP_NOTIFICATION_CENTER_POLL_INTERVAL_MS);
  if (!Number.isFinite(configured) || configured < 15_000 || configured > 300_000) {
    return DEFAULT_NOTIFICATION_POLL_INTERVAL_MS;
  }
  return Math.round(configured);
}

export function useNotificationCenterFeature() {
  const { user } = useAuth();
  const registry = useOptionalFeatureRegistry();
  const feature = registry?.getFeature?.(NOTIFICATION_CENTER_FEATURE_KEY) || null;
  const loading = !registry || registry.source === "loading";
  const ready = registry?.ready === true;
  const runtimeStatusAllowed = NOTIFICATION_CENTER_RUNTIME_STATUSES.has(
    feature?.functionalStatus
  );
  const privateBetaAllowed = feature?.functionalStatus !== "PRIVATE_BETA" || Boolean(
    user?.is_staff
    || user?.is_superuser
  );

  return {
    feature,
    loading,
    ready,
    enabled: ready
      && runtimeStatusAllowed
      && isFeatureNavigable(feature)
      && privateBetaAllowed,
    privateBetaAllowed,
    runtimeStatusAllowed,
    error: registry?.error || "",
    source: registry?.source || "unavailable",
  };
}

export function NotificationCenterRuntime({ children }) {
  useSourceLanguage();
  const { user } = useAuth();
  const featureState = useNotificationCenterFeature();
  const accountKey = String(user?.id || user?.username || "").trim();
  const enabled = Boolean(accountKey && featureState.enabled);

  if (!enabled) return children;

  return (
    <NotificationCenterProvider
      key={accountKey}
      enabled={enabled}
      accountKey={accountKey}
      pollIntervalMs={configuredPollInterval()}
    >
      {children}
    </NotificationCenterProvider>
  );
}

export default function NotificationFeatureGate({ children }) {
  useSourceLanguage();
  const featureState = useNotificationCenterFeature();

  if (featureState.loading) {
    return (
      <main className="notification-feature-state">
        <p role="status" aria-live="polite"><LocalizedText source={"Vérification du centre de notifications…"} /></p>
      </main>
    );
  }

  if (!featureState.ready && featureState.error) {
    return (
      <main className="notification-feature-state">
        <p role="alert"><LocalizedText source={"Le statut du centre de notifications est momentanément indisponible."} /></p>
      </main>
    );
  }

  if (!featureState.enabled) {
    return <Navigate to="/mes-eternua" replace />;
  }

  return children;
}
